/**
 * Trello scope detection utilities for MCP consent.
 *
 * Trello scopes follow the pattern `action:resource:trello` (e.g. `read:board:trello`).
 *
 * When the consent token contains Trello scopes, the MCP consent screen shows a
 * Trello workspace selector alongside (or instead of) the Atlassian site selector.
 */

/** Returns true if the scope targets the Trello product. */
export function isTrelloScope(scope: string): boolean {
  return scope.endsWith(':trello');
}

/** Returns true if any scope in the list is a Trello scope. */
export function hasTrelloScopes(scopes: readonly string[]): boolean {
  return scopes.some(isTrelloScope);
}

// Default scopes that are always present and should not count as
// "Atlassian product scopes" for selector purposes.
const DEFAULT_SCOPES = new Set(['read:me', 'read:account', 'offline_access']);

/** Returns true if any scope in the list is a non-Trello (Atlassian) scope. */
export function hasAtlassianScopes(scopes: readonly string[]): boolean {
  return scopes.some((s) => !isTrelloScope(s) && !DEFAULT_SCOPES.has(s));
}

/** Classification of which lines of business are present in the scope set. */
export type ScopeClassification = {
  hasTrello: boolean;
  hasAtlassian: boolean;
};

/** Classify a scope list into Trello / Atlassian presence flags. */
export function classifyScopes(scopes: readonly string[]): ScopeClassification {
  return {
    hasTrello: hasTrelloScopes(scopes),
    hasAtlassian: hasAtlassianScopes(scopes),
  };
}

/**
 * Classify scopes with an authoritative server-side override for Trello access.
 *
 * The Trello workspace selector is shown only when `hasTrelloProductAccess` is
 * explicitly `true` **and** the token contains at least one Trello scope.
 * Any falsy value (`false`, `null`, `undefined`) hides the selector regardless
 * of scopes.
 *
 * When `mixedWorkspaceGateEnabled` is `false` (the default) and the token
 * contains both Atlassian and Trello scopes, `hasTrello` is forced to `false`
 * so that the Trello workspace selector is hidden in mixed-scope flows.
 */
export function classifyScopesWithOverride(
  scopes: readonly string[],
  hasTrelloProductAccess: boolean | null | undefined,
  mixedWorkspaceGateEnabled = false,
): ScopeClassification {
  const hasAtlassian = hasAtlassianScopes(scopes);
  const hasTrello = hasTrelloProductAccess === true && hasTrelloScopes(scopes);

  return {
    hasTrello: hasTrello && (mixedWorkspaceGateEnabled || !hasAtlassian),
    hasAtlassian,
  };
}
